import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { AvatarModule } from 'primeng/avatar';

@Component({
  selector: 'app-user-avatar',
  imports: [AvatarModule],
  templateUrl: './user-avatar.component.html',
  styleUrl: './user-avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent {
  readonly fullName = input<string>('');
  readonly avatarImage = input<string>('');
  readonly size = input<'normal' | 'large' | 'xlarge'>('normal');
  readonly backgroundColor = input<string>('');
  readonly foregroundColor = input<string>('white');

  // Color palette that contrasts well with white text
  private readonly colorPalette = [
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#eab308', // yellow-500
    '#22c55e', // green-500
    '#06b6d4', // cyan-500
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#f59e0b', // amber-500
    '#10b981', // emerald-500
    '#6366f1', // indigo-500
    '#8b5cf6', // purple-500
    '#e11d48', // rose-500
    '#0ea5e9', // sky-500
    '#84cc16', // lime-500
    '#f97316', // orange-500
    '#6b7280', // gray-500
    '#7c3aed', // violet-600
    '#dc2626', // red-600
    '#059669', // emerald-600
    '#7c2d12', // orange-900
    '#991b1b', // red-800
    '#1e40af', // blue-700
    '#7e22ce', // purple-700
    '#be185d', // pink-700
    '#a21caf', // fuchsia-700
  ];

  // Generate initials from full name
  readonly initials = computed(() => {
    const name = this.fullName().trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const first = parts[0]?.charAt(0) ?? '';
      const last =
        (parts.length > 1 ? parts[parts.length - 1] : parts[0])?.charAt(0) ??
        '';
      return (first + last).toUpperCase();
    }
    return 'NA';
  });

  // Generate background color based on first letter of name
  readonly computedBackgroundColor = computed(() => {
    if (this.backgroundColor()) {
      return this.backgroundColor();
    }

    const name = this.fullName().trim();
    if (name) {
      const firstChar = name.charAt(0).toLowerCase();
      const charCode = firstChar.charCodeAt(0);
      const index = charCode >= 97 && charCode <= 122 
        ? (charCode - 97) % this.colorPalette.length 
        : 0;
      return this.colorPalette[index];
    }
    
    return '#3b82f6'; // default blue
  });

  // Computed style object for the avatar
  readonly avatarStyle = computed(() => ({
    'background-color': this.avatarImage() ? 'transparent' : this.computedBackgroundColor(),
    'color': this.foregroundColor()
  }));
}
